// frontend/src/components/plot/charts/WindChart.js
import React, { useMemo } from 'react';
import { ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Brush, Customized } from 'recharts';
import { getLighterColor } from '../../../utils/colorUtils';
import { getFilteredChartData, formatXAxisTick, computeWindSpeedAndDirection, getWindCompassCardinals } from './chartUtils';

export default function WindChart({ chartData, visibleDevices, forecastVisible = true, localTimeEnabled = true }) {
  const enrichedData = useMemo(() => {
    if (!Array.isArray(chartData)) return [];
    return chartData.map((pt) => {
      const copy = { ...pt };
      visibleDevices.forEach((dev) => {
        ['hist', 'fcst'].forEach((mode) => {
          const uKey = `wind_u_${dev.id}_${mode}`;
          const vKey = `wind_v_${dev.id}_${mode}`;
          if (copy[uKey] !== undefined && copy[vKey] !== undefined) {
            const { speed, direction } = computeWindSpeedAndDirection(copy[uKey], copy[vKey]);
            copy[`wind_speed_${dev.id}_${mode}`] = speed;
            copy[`wind_direction_${dev.id}_${mode}`] = direction;
          }
        });
      });
      return copy;
    });
  }, [chartData, visibleDevices]);

  const filteredData = useMemo(() => getFilteredChartData(enrichedData, forecastVisible), [enrichedData, forecastVisible]);

  const hasWindData = visibleDevices.some((dev) =>
    filteredData.some((pt) => pt[`wind_speed_${dev.id}_hist`] !== undefined || pt[`wind_speed_${dev.id}_fcst`] !== undefined)
  );

  if (!hasWindData) return null;

  return (
    <div style={{ marginBottom: '20px' }}>
      <h5 style={{ margin: '0 0 8px 0', color: '#334155' }}>Wind Speed & Direction</h5>
      <div style={{ width: '100%', height: '280px', position: 'relative' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={filteredData}>
            <CartesianGrid strokeDasharray="2 2" stroke="#f1f5f9" />
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(ts) => formatXAxisTick(ts, localTimeEnabled)}
              minTickGap={30}
              tick={{ fontSize: 10 }}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              domain={[0, 'auto']}
              unit=" m/s"
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null;
                const timeStr = label ? (localTimeEnabled ? new Date(label).toLocaleString() : new Date(label).toUTCString()) : '';
                return (
                  <div style={{ background: '#ffffff', border: '1px solid #cbd5e1', padding: '12px', borderRadius: '8px', fontSize: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', maxWidth: '280px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '8px', color: '#1e293b', borderBottom: '1px solid #f1f5f9', paddingBottom: '4px' }}>
                      {timeStr}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {payload.map((item, idx) => {
                        const parts = String(item.name || '').split('\n');
                        const baseName = parts[0] || item.dataKey;
                        const devName = parts[1] || '';
                        const speedVal = item.value;

                        const dataPt = item.payload;
                        const matchDevId = visibleDevices.find(d => item.dataKey.includes(d.id))?.id;
                        const mode = item.dataKey.includes('_fcst') ? 'fcst' : 'hist';
                        const dirVal = matchDevId ? dataPt[`wind_direction_${matchDevId}_${mode}`] : null;
                        const compass = getWindCompassCardinals(dirVal);

                        return (
                          <div key={`wind-tip-${idx}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color, display: 'inline-block', marginTop: '3px', flexShrink: 0 }} />
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontWeight: 600, color: '#0f172a' }}>
                                {baseName}: <strong>{speedVal} m/s</strong> {dirVal !== null ? `(${dirVal}° ${compass})` : ''}
                              </span>
                              {devName && (
                                <span style={{ fontSize: '11px', color: '#64748b' }}>
                                  {devName}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }}
            />
            <Brush dataKey="time" height={24} stroke="#94a3b8" fill="#f8fafc" />

            <Customized component={(props) => {
              const { xAxisMap } = props;
              if (!xAxisMap || filteredData.length === 0) return null;
              const xScale = xAxisMap[Object.keys(xAxisMap)[0]]?.scale;
              if (!xScale) return null;

              const step = Math.max(1, Math.floor(filteredData.length / 10));
              const primaryDev = visibleDevices[0];
              if (!primaryDev) return null;

              const arrows = [];
              for (let i = 0; i < filteredData.length; i += step) {
                const pt = filteredData[i];
                const xCoord = xScale(pt.timestamp);
                const dirVal = pt[`wind_direction_${primaryDev.id}_hist`] ?? pt[`wind_direction_${primaryDev.id}_fcst`];
                if (xCoord !== undefined && dirVal !== null && dirVal !== undefined) {
                  arrows.push(
                    <g key={`arrow-${i}`} transform={`translate(${xCoord}, 18)`}>
                      <path
                        d="M 0,-7 L 4,3 L 0,1 L -4,3 Z"
                        fill={primaryDev.color}
                        transform={`rotate(${dirVal})`}
                      />
                    </g>
                  );
                }
              }
              return <g>{arrows}</g>;
            }} />

            {visibleDevices.map((dev) => {
              const fcstColor = getLighterColor(dev.color, 40);
              return (
                <React.Fragment key={dev.id}>
                  <Line
                    type="monotone"
                    dataKey={`wind_speed_${dev.id}_hist`}
                    name={`Wind Speed\n(${dev.name})`}
                    stroke={dev.color}
                    strokeWidth={2}
                    dot={{ r: 3, shape: 'circle' }}
                    connectNulls={true}
                  />
                  {forecastVisible && (
                    <Line
                      type="monotone"
                      dataKey={`wind_speed_${dev.id}_fcst`}
                      name={`Wind Speed forecast\n(${dev.name})`}
                      stroke={fcstColor}
                      strokeWidth={2}
                      dot={{ r: 3, shape: 'diamond' }}
                      connectNulls={true}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
